function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target && event.target.matches('input[type="checkbox"]')) {
        this.onSubmitHandler(event);
        return;
      }

      if (event.target && event.target.matches('price-range input[type="number"]')) {
        return;
      }

      this.debouncedOnSubmit(event);
    });
    facetForm.addEventListener('change', (event) => {
      if (
        event.target &&
        (event.target.tagName === 'SELECT' || event.target.matches('price-range input[type="number"]'))
      ) {
        this.debouncedOnSubmit(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);

  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static preserveViewParam(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    const currentView = new URLSearchParams(window.location.search).get('view');

    if (params.has('view')) {
      const view = params.get('view');
      params.delete('view');
      if (view) params.set('view', view);
    }

    if (currentView && !params.has('view')) {
      params.set('view', currentView);
    }

    return params.toString();
  }

  static removeDefaultPriceValues(form, formData) {
    form.querySelectorAll('[data-default-filter-value]').forEach((input) => {
      if (!input.name || input.value !== input.dataset.defaultFilterValue) return;
      formData.delete(input.name);
    });
  }

  static createSearchParamsFromForm(form) {
    const formData = new FormData(form);
    FacetFiltersForm.removeDefaultPriceValues(form, formData);
    return new URLSearchParams(formData);
  }

  static mergeSearchParams(searchParamsList) {
    const mergedParams = new URLSearchParams();
    const singletonKeys = ['q', 'type', 'options[prefix]', 'sort_by'];

    searchParamsList.forEach((searchParams) => {
      new URLSearchParams(searchParams).forEach((value, key) => {
        if (key === 'view') return;
        if (singletonKeys.includes(key)) {
          if (value) mergedParams.set(key, value);
          return;
        }
        mergedParams.append(key, value);
      });
    });

    return FacetFiltersForm.preserveViewParam(mergedParams.toString());
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.preserveViewParam(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionGrid = productGridContainer ? productGridContainer.querySelector('.collection') : null;
    const isSortUpdate = event?.target?.closest('form')?.id === 'FacetSortForm';

    if (collectionGrid && !isSortUpdate) {
      collectionGrid.classList.add('loading');
    }

    productGridContainer?.classList.toggle('is-sort-updating', isSortUpdate);

    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const parsedProductGridContainer = parsedHTML.getElementById('ProductGridContainer');

    if (!productGridContainer || !parsedProductGridContainer) return;

    const isSortUpdate = productGridContainer.classList.contains('is-sort-updating');

    if (isSortUpdate) {
      parsedProductGridContainer.querySelectorAll('img.lazyload, img.lazyloading').forEach((image) => {
        image.classList.remove('lazyload', 'lazyloading');
        image.classList.add('lazyloaded');
      });
    }

    productGridContainer.innerHTML = parsedProductGridContainer.innerHTML;
    productGridContainer.classList.remove('is-sort-updating');
    productGridContainer.querySelector('.collection')?.classList.remove('loading');
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const parsedCountContainer = parsedHTML.getElementById('ProductCount');
    if (!parsedCountContainer) return;

    const count = parsedCountContainer.innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    if(window.WhatSub) {
      window.WhatSub.dispatch('UPDATE_FILTERS');
      }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.m-sort'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      if (!sourceElement) return;
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;

      if (selector === '.m-sort') {
        targetElement.outerHTML = sourceElement.outerHTML;
        window.syncNativeSortDisplay?.();
        return;
      }

      targetElement.innerHTML = sourceElement.innerHTML;
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    const sourceShowResult = html.querySelector('#FacetFiltersFormMobile .m-mobile-facets__show-result');
    if (sourceShowResult) {
      document.querySelectorAll('#FacetFiltersFormMobile .m-mobile-facets__show-result').forEach((button) => {
        button.textContent = sourceShowResult.textContent;
        if (sourceShowResult.dataset.resultCount) {
          button.dataset.resultCount = sourceShowResult.dataset.resultCount;
        }
      });
    }

    const mobileFacetDrawer = mobileFacetForm ? mobileFacetForm.closest('menu-drawer') : null;

    if (mobileFacetDrawer && typeof mobileFacetDrawer.bindEvents === 'function') {
      mobileFacetDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetMobileSummary = target.querySelector(':scope > summary .m-mobile-facets__filter');
    const sourceMobileSummary = source.querySelector(':scope > summary .m-mobile-facets__filter');
    if (targetMobileSummary && sourceMobileSummary) {
      targetMobileSummary.innerHTML = sourceMobileSummary.innerHTML;
    }

    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    return FacetFiltersForm.preserveViewParam(FacetFiltersForm.createSearchParamsFromForm(form).toString());
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    event.target.closest('.m-facets__checkbox')?.classList.add('is-pending');
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.matches('.mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(FacetFiltersForm.createSearchParamsFromForm(form).toString());
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(FacetFiltersForm.createSearchParamsFromForm(form).toString());
        }
      });
      this.onSubmitForm(FacetFiltersForm.mergeSearchParams(forms), event)
    }
    if(window.WhatSub) {
      window.WhatSub.dispatch('REMOVE_FACETS');
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
    if(window.WhatSub) {
      window.WhatSub.dispatch('REMOVE_ACTIVE_FILTERS');
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);

  }
}

customElements.define('facet-remove', FacetRemove);
